import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectService } from '../../../api/services/project.services';
import { MarketOrderInput, MarketOrder, MarketOrderSavedFilter } from '../../../api/models/market-order-summary';
import { UserService } from '../../../api/services/user.service';
import { PaginatedRequest } from '../../../api/models/paginated-request';
import { NgbAccordion, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Location } from '@angular/common';
import { PreferencesService } from '../../../api/services/preferences.service';
import { Market } from '../../../api/models/market-model';
import { MarketService } from '../../../api/services/market.service';
import { DeleteModalComponent } from '../../delete-modal/delete-modal.component';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../api/services/auth.service';
import { ProjectCreationComponent } from '../../project-creation/project-creation.component';
import { Project } from '../../../api/models/project-model';
import { Worksheet } from '../../../api/models/worksheet';
import { NgxSpinnerService } from 'ngx-spinner';
import { OrderCreateComponent } from '../../order-create/order-create.component';
import { ProjectOrderInput } from '../../../api/models/project-summary';
import { UserInfo } from '../../../api/models/user-model';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-market-order-summary',
  templateUrl: './market-order-summary.component.html',
  styleUrls: ['./market-order-summary.component.scss']
})
export class MarketOrderSummaryComponent implements OnInit {

  marketId: string;
  market: Market;
  loading: boolean = false;
  errorMessage: string[] = [];
  paginatedRequest: PaginatedRequest;
  page: number = 1;
  pageSize: number = 30;
  sorting: string = 'updateDateTime,desc';
  marketOrders: MarketOrder[];
  isExpandAll : boolean = false;
  isSelectAll : boolean = false;
  isAnySelected: boolean = false;
  totalRecords: number = 0;
  specialSelected: string;
  initiatorUsers: UserInfo[] = [];
  approverUsers: UserInfo[] = [];
  currentSortField: string = 'updateDateTime';
  currentSortOrder: string = 'desc';
  currentStatusFilter: string = 'ALL';
  currentOwnerFilter: string = '';
  currentApproverFilter: string = '';
  selectedWorksheetIds: string[] = [];
  disableExportButton: boolean = false;
  blob: any;
  err: string[] = [];
  creationError = false;
  confirmedOrder = {};
  hasCreateProjectPermission: boolean =  false;
  showSavedFilters: boolean = false;
  marketOrderSavedFilterList: MarketOrderSavedFilter[] = [];
  selectedMosFilter: MarketOrderSavedFilter;
  searchSubject: Subject<string> = new Subject<string>();
  searchText: string;
  emptySearchString: boolean = true;
  highlightFilterBackground: boolean = false;

  worksheetStatuses: {status: string, value: string}[] = [];
  @ViewChild('accordion') accordion: NgbAccordion;

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private userService: UserService,
    private authService: AuthService,
    private location: Location,
    private marketService: MarketService,
    private preferencesService: PreferencesService,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private elementRef: ElementRef
    ) {
      this.searchSubject.asObservable()
      .debounceTime(1000)
      .subscribe(searchText => {
        if (searchText) {
          this.searchText = searchText;
        }
        this.loadPage(1);
      });
     }

  ngAfterViewInit() {
    const hostElem = this.elementRef.nativeElement;
    hostElem.parentNode.parentNode.parentNode.parentNode.parentNode.setAttribute('style', 'max-width: 1400px !important');
  }

  ngOnDestroy() {
    const hostElem = this.elementRef.nativeElement;
    hostElem.parentNode.parentNode.parentNode.parentNode.parentNode.removeAttribute('style');
  }


  loadPage(page: number) {
    this.page = page;
    this.paginatedRequest = new PaginatedRequest(this.page, this.pageSize, this.sorting);
    this.projectService.getMarketOrderSummary(new MarketOrderInput(this.marketId, this.currentOwnerFilter.length ? this.currentOwnerFilter : null,
      this.currentApproverFilter.length ? this.currentApproverFilter : null, this.currentStatusFilter, this.searchText)
      , this.paginatedRequest).subscribe(result => {
      if (result){
        this.marketOrders = result.records;
        this.totalRecords = result.totalRecords;
        if (this.marketOrders) {
          this.marketOrders.forEach(mo => mo.checked=false);
        }
      } else {
        this.marketOrders = [];
        this.totalRecords = 0;
      }
      this.loading = false;
      this.isExpandAll = false;
      this.accordion.activeIds=[];
      this.deselectAllWorksheets();
    },error => {
      this.loading = false;
      this.errorMessage.push("Something went wrong while fetching the market order summary !!!");
    });
  }

  ngOnInit() {
    this.route.queryParams.subscribe(queryParams => {
      if (queryParams['currentStatusFilter']) {
        this.currentStatusFilter = queryParams['currentStatusFilter'];
      }
      if (queryParams['currentOwnerFilter']) {
        this.currentOwnerFilter = queryParams['currentOwnerFilter'];
      }
      if (queryParams['currentApproverFilter']) {
        this.currentApproverFilter = queryParams['currentApproverFilter'];
      }
    });
    this.route.params.subscribe(params => {
      this.loading = true;
      this.marketId = params.id;
      if (this.userService.isMarketPermittedForCurrentUser(this.marketId)) {
        this.marketService.getMarket(this.marketId).subscribe(mkt => {
          this.market = mkt;
          if (this.market) {
            this.preferencesService.getPreferences('CUSTOMER', this.market.customerId).subscribe(prefs => {
              this.specialSelected = prefs['special-fields'].Value;
              this.setValueOfWorksheetStatus();
              if (this.specialSelected !== 'ATC') {
                this.userService.findUserById(this.authService.getUserId()).subscribe(user => {
                  const context = `ORGANIZATION~${user['Organization ID']}`;
                  this.authService.getUserRolesInContext(context, false).subscribe(roles => {
                    this.hasCreateProjectPermission = roles.includes('PROJECT_CREATE');
                  });
                });
                this.projectService.getWorksheetInitiatorsIdByMarket(this.market.id).subscribe(initiatorIds => {
                  if (initiatorIds) {
                    this.userService.getUserDisplayNames(initiatorIds).subscribe(initiatorUsers => {
                      this.initiatorUsers = initiatorUsers;
                    });
                  }
                });
                this.projectService.getWorksheetApproversIdByMarket(this.market.id).subscribe(approverIds => {
                  if (approverIds) {
                    this.userService.getUserDisplayNames(approverIds).subscribe(approverUsers => {
                      this.approverUsers = approverUsers;
                    });
                  }
                });
                this.loadPage(1);
                this.loadMosFilters();
              }
            });
          }
        }, error => {
          this.loading = false;
          this.errorMessage.push(error.error.message);
        });
      } else {
        this.loading = false;
        this.errorMessage.push("You don't have permission to view this page!!!");
      }
    });
  }

  isOpened(index: number) {
    const id = `ngb-panel-${index + 1}`;
    return this.accordion && this.accordion.activeIds && this.accordion.activeIds.includes(id);
  }

  goBack(): void {
    this.location.back();
  }

  expandCollapseAll() {
    if (this.isExpandAll) {
      this.isExpandAll = false;
      this.accordion.activeIds=[];
    } else {
      this.isExpandAll = true;
      if (this.marketOrders && this.marketOrders.length) {
        let activeIds : string[] =[];
        this.marketOrders.forEach((order, index) => {
          order.marketOrderDetails && order.marketOrderDetails.length && activeIds.push(`ngb-panel-${index + 1}`);
        })
        this.accordion.activeIds = activeIds;
      }
    }
  }

  doSort(sortField:string) {
    if (this.currentSortOrder === ''|| sortField !== this.currentSortField) {
      this.currentSortOrder = 'asc';
      this.sorting = `${sortField},${this.currentSortOrder}`;
    } else if (this.currentSortOrder === 'asc') {
      this.currentSortOrder = 'desc';
      this.sorting = `${sortField},${this.currentSortOrder}`;
    } else {
      this.currentSortOrder = '';
      this.sorting = '';
    }
    this.currentSortField = sortField;
    this.loadPage(1);
  }

  filterByOwner(initiatorId: string) {
    this.loading = true;
    this.currentOwnerFilter = initiatorId;
    this.loadPage(1);
  }

  filterByApprover(approverId: string) {
    this.loading = true;
    this.currentApproverFilter = approverId;
    this.loadPage(1);
  }

  filterByStatus(status: string) {
    this.loading = true;
    this.currentStatusFilter = status;
    this.loadPage(1);
  }

  selectWorksheet(wId: string, flag: boolean) {
    if (this.marketOrders) {
      this.marketOrders.forEach(mo => {
        if (mo.worksheetId === wId) {
            mo.checked = flag;
        }
      });
      if (this.marketOrders.some(mo => mo.checked)) {
        this.isAnySelected = true;
      } else {
        this.isAnySelected = false;
      }
      if (this.marketOrders.every(mo => mo.checked)) {
        this.isSelectAll = true;
      } else {
        this.isSelectAll = false;
      }
    }
  }

  selectAllWorksheets(flag: boolean) {
    if (this.marketOrders) {
      if (flag) {
        this.marketOrders.forEach(val => { val.checked = true });
        this.isSelectAll = true;
        this.isAnySelected = true;
      } else {
        this.marketOrders.forEach(val => { val.checked = false });
        this.isSelectAll = false;
        this.isAnySelected = false;
      }
    }
  }

  deselectAllWorksheets() {
    if (this.marketOrders) {
      this.marketOrders.forEach(val => { val.checked = false });
      this.isSelectAll = false;
      this.isAnySelected = false;
    }
  }

  preventToggle(event: any) {
    event.stopPropagation();
    event.preventDefault();
  }

  doToggle(index: number) {
    if (this.accordion && this.accordion.activeIds) {
      const id = `ngb-panel-${index + 1}`;
      if(this.accordion.activeIds instanceof Array) {
        if (this.accordion.activeIds.includes(id)) {
          this.accordion.activeIds.splice(this.accordion.activeIds.indexOf(id), 1);
        } else {
          this.accordion.activeIds.push(id);
        }
      } else {
        if (this.accordion.activeIds === id) {
          this.accordion.activeIds = null;
        } else {
          this.accordion.activeIds = id;
        }
      }
    }
  }

  deleteSelectedWorksheets() {
    const modalRef = this.modalService.open(DeleteModalComponent, {
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.bodyMsg = `Are you sure you want to delete the selected worksheets ?`;
    modalRef.componentInstance.fn = () => {
      this.loading = true;
      this.projectService.deleteWorksheets(this.marketOrders.filter(mo => mo.checked === true)
          .map(mo => mo.worksheetId), this.marketId).subscribe(data => {
        modalRef.close();
        this.onSuccess('Worksheets deleted sucessfully!!!');
        this.loadPage(1);
      }, error => {
        this.loading = false;
        modalRef.close();
        this.onFailure('Worksheets could not be deleted. Please ensure that you are having permission.!!!');
      });
    };
  }

  private onSuccess(successsMessage) {
    this.toastr.success(successsMessage, 'OK!', {
        timeOut: 5000,
        positionClass: 'toast-bottom-right'
    });
  }

  private onFailure(failureMessage) {
      this.toastr.error(failureMessage, 'Error!', {
          timeOut: 2000,
          positionClass: 'toast-bottom-right'
      });
  }

  private showInfoToastr(infoMessage) {
    this.toastr.info(infoMessage, 'Info!', {
        timeOut: 2000,
        positionClass: 'toast-bottom-right'
    });
  }

 downloadXls(){
  this.loading = true;
  this.disableExportButton = true;
  let marketOrderInputs = new MarketOrderInput(this.marketId,
      this.currentOwnerFilter.length ? this.currentOwnerFilter : null,
      this.currentApproverFilter.length ? this.currentApproverFilter : null,
      this.currentStatusFilter);

  this.projectService.downloadMarketOrderSummary(marketOrderInputs).subscribe((resp) => {
      const fileName = resp.headers.get('filename') ? resp.headers.get('filename') : 'MarketOrder_Summary_' + new Date();
      const data = resp.body;
      this.blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      var downloadUrl = window.URL.createObjectURL(data);
      var link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName + '.xlsx';
      link.click();
      this.loading = false;
      this.disableExportButton = false;
  }, error => {
      this.onFailure("Error downloading the file!!!");
      this.loading = false;
      this.disableExportButton = false;
  });
 }

  createNewSite() {
    const modalRef = this.modalService.open(ProjectCreationComponent, {
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.project = new Project();
    modalRef.componentInstance.specialSelected = this.specialSelected;
    modalRef.componentInstance.action = 'New';

    modalRef.result.then((result) => {
      if (this.specialSelected !== 'ATC' && result) {
        this.createWorksheet(result);
      }
    }).catch(() => { 
      //Empty catch block
    });
  }

  createWorksheet(project: Project) {
    if (project) {
      let worksheet = new Worksheet();
      worksheet.projectId = project.id;
      worksheet.siteName = project.name
      worksheet.siteNumber = '0000';
      worksheet.requestedDeliveryDate = project.deliveryDate;
      worksheet.contractorId = null;
      worksheet.confirmedNeedByDate = false;

      let addressSelectedId = null;
      this.preferencesService.getPreferences('PROJECT', project.id).subscribe(projectPrefs => {
        addressSelectedId = projectPrefs['site-address'].Value;
        if (!addressSelectedId) {
          this.preferencesService.getPreferences('MARKET', project.marketId).subscribe(marketPrefs => {
            addressSelectedId = marketPrefs['default-address'].Value;
          });
        }
      });

      if(addressSelectedId){
        worksheet.shipToAddressId = addressSelectedId;
        worksheet.confirmedShipToAddressId = true;
      } else {
        worksheet.shipToAddressId = null;
        worksheet.confirmedShipToAddressId = false;
      }

      const modalRef = this.modalService.open(OrderCreateComponent, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false
      });    
      modalRef.componentInstance.project = project;
      modalRef.componentInstance.worksheet = worksheet;
      modalRef.componentInstance.action = 'Create';

      this.loadPage(1);
    }
  }

  viewExistingSites(){
    this.router.navigate(['/dashboard', { outlets: { 'dashboard-content': 'my-oasis-tiled' } }]);
  }

  onErrorAction(error: any) {
    this.creationError = true;
    this.err.push(error.error.message);
  }

  clearError(){
    this.creationError = false;
    this.err = [];
  }

  openItem(order: MarketOrder) {
    if (!order.quoted && (order.orderNumber === undefined || order.orderNumber === null)) {
      this.router.navigate(['/dashboard', { outlets: { 'dashboard-content': ['order-worksheet', order.worksheetId] } }],
        { queryParams: { currentStatusFilter: this.currentStatusFilter, currentOwnerFilter: this.currentOwnerFilter, currentApproverFilter: this.currentApproverFilter } });
    } else if (order.orderNumber === undefined || order.orderNumber === null) {
      this.router.navigate(['/quote-process', { outlets: { 'quote-process-content': ['checkout', order.worksheetId] } }]);
    } else {
      this.projectService.getOrderConfirmationV2(new ProjectOrderInput(order.projectName, this.market.accountNumber, order.orderNumber, localStorage.getItem("marketLocationCode"))).subscribe(result => {
        this.confirmedOrder = result.orders[0];
        if(this.confirmedOrder && order.projectName && this.market.accountNumber){
          this.router.navigate(['/dashboard', { outlets: { 'dashboard-content': ['order-confirmation', order.projectName, this.market.accountNumber, order.orderNumber] } }]);
        }else {
          this.router.navigate(['/quote-process', { outlets: { 'quote-process-content': ['confirmedOrder', order.worksheetId] } }]);
        }
      });

    }
  }
  loadMosFilters() {
    this.projectService.getUserMosSavedFilters(this.marketId).subscribe(mosFilters => {
      if (mosFilters.length > 0) {
        this.marketOrderSavedFilterList = mosFilters;
      }
    });
  }

  checkForFilterCombination(){
    let isCombinationPresent = false;
    let currentFilterCombination = this.setMosFilterData();

    for(let mosSavedFilter of this.marketOrderSavedFilterList){
      if(mosSavedFilter.mosWorksheetStatus === currentFilterCombination.mosWorksheetStatus
          && mosSavedFilter.mosWorksheetOwnerName === currentFilterCombination.mosWorksheetOwnerName
            && mosSavedFilter.mosApproverName === currentFilterCombination.mosApproverName){
        isCombinationPresent = true;
        this.highlightFilterBackground = true;
        this.selectedMosFilter = mosSavedFilter;
        this.showInfoToastr('Selected filter combination is already present.')
        break;
      }
    }

    return isCombinationPresent;
  }

  saveMosFilter() {
    if(!this.checkForFilterCombination()){
      this.projectService.saveNewMosFilter(this.setMosFilterData()).subscribe(savedMosFilter => {
        this.selectedMosFilter = savedMosFilter;
        this.loadMosFilters();
      });
    }
  }

  updateMosFilter() {
    if(!this.checkForFilterCombination()){
      this.projectService.updateMosFilter(this.setMosFilterData(this.selectedMosFilter.id)).subscribe(savedMosFilter => {
        this.selectedMosFilter = savedMosFilter;
        this.loadMosFilters();
      });
    }
  }

  setMosFilterData(id?: string) {
    let mosSavedFilter = new MarketOrderSavedFilter();
    mosSavedFilter.marketId = this.marketId;
    if (id) {
      mosSavedFilter.id = id;
      mosSavedFilter.userId = this.selectedMosFilter.userId;
    }
    mosSavedFilter.mosWorksheetStatusValue = this.currentStatusFilter;
    mosSavedFilter.mosWorksheetStatus = this.worksheetStatuses.find(worksheetStatus => worksheetStatus.value === this.currentStatusFilter).status;
    mosSavedFilter.mosWorksheetOwnerId = this.currentOwnerFilter;
    if (this.currentOwnerFilter && this.currentOwnerFilter !== '') {
      mosSavedFilter.mosWorksheetOwnerName = this.initiatorUsers.find(initiator => initiator.userId === this.currentOwnerFilter).displayName;
    } else {
      mosSavedFilter.mosWorksheetOwnerName = '';
    }
    mosSavedFilter.mosApproverId = this.currentApproverFilter;
    if (this.currentApproverFilter && this.currentApproverFilter !== '') {
      mosSavedFilter.mosApproverName = this.approverUsers.find(approver => approver.userId === this.currentApproverFilter).displayName;
    } else {
      mosSavedFilter.mosApproverName = '';
    }
    return mosSavedFilter;
  }

  setFilters(filter: MarketOrderSavedFilter) {
    this.highlightFilterBackground = true;
    if (this.selectedMosFilter && this.selectedMosFilter.id === filter.id) {
      this.selectedMosFilter = null
    } else {
      this.selectedMosFilter = filter;
      this.currentStatusFilter = this.selectedMosFilter.mosWorksheetStatusValue;
      this.currentOwnerFilter = this.selectedMosFilter.mosWorksheetOwnerId ? this.selectedMosFilter.mosWorksheetOwnerId : '';
      this.currentApproverFilter = this.selectedMosFilter.mosApproverId ? this.selectedMosFilter.mosApproverId : '';
      this.loading = true;
      this.loadPage(1);
    }
  }

  setFilterBackground(id: string) {
    if (this.selectedMosFilter && this.selectedMosFilter.id === id) {
      return 'selected';
    } else {
      return '';
    }
  }

  searchMOS(value) {
    this.page = 1;
    if (value && value.trim().length > 0) {
      if (this.searchText !== value.trim()) {
        this.emptySearchString = false;
        this.searchSubject.next(value);
      }
    } else {
      this.searchText = "";
      if (!this.emptySearchString) {
        this.emptySearchString = true;
      }
      this.loadPage(this.page);
    }
  }

  onPaste(event: ClipboardEvent) {
    let clipboardData = event.clipboardData;
    let pastedText = clipboardData.getData('text');
    this.searchMOS(pastedText);
  }

  setValueOfWorksheetStatus() {
    if (this.specialSelected === 'Verizon eQuote') {
      this.worksheetStatuses = [
        { status: 'All', value: 'ALL' },
        { status: 'Incomplete', value: 'NONE' },
        { status: 'Pending Approval', value: 'PENDING_APPROVAL' },
        { status: 'Revision Requested by Approver', value: 'DENIED' },
        { status: 'Revision Requested by TESSCO', value: 'DENIED_BY_TESSCO' },
        { status: 'TESSCO Review', value: 'TESSCO_REVIEW' },
        { status: 'Quoted, awaiting PO', value: 'QUOTED_AWAITING_PO' },
        { status: 'TESSCO Review Complete', value: 'TESSCO_REVIEW_COMPLETE' },
        { status: 'REC(A)', value: 'REC_A' }
      ];
    } else if (this.specialSelected === 'Standard') {
      this.worksheetStatuses = [
        { status: 'All', value: 'ALL' },
        { status: 'Incomplete', value: 'NONE' },
		    { status: 'Pending Approval', value: 'PENDING_APPROVAL' },
        { status: 'Approved', value: 'APPROVED' },
        { status: 'Revision Requested by Approver', value: 'DENIED' },
        { status: 'Quoted, awaiting PO', value: 'QUOTED_AWAITING_PO' },
        { status: 'Order Placed', value: 'ORDER_PLACED' },
      ];
    }
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/app/dashboard-my-oasis/market-order-summary/market-order-summary.component.ts